import { useState } from 'react';
import { Helmet } from 'react-helmet';
import { useHistory } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';

import { Button, Space, Modal, Divider } from 'antd';

import { flatInfiniteData } from '@/utils/request/flatInfiniteData';

import { ListNcmsQuery, Ncm } from '@/core/domain/operational/Ncm/Ncm.types';
import { useNcms } from '@/core/domain/operational/Ncm/Ncm.hooks';

import { PageProps } from '@/types/PageProps';

import AppPageHeader from '@/components/_common/AppPageHeader';
import InfiniteTable, { ColumnsType } from '@/components/_common/InfiniteTable';

import NcmReportModal from '@/components/Fiscal/Ncm/NcmForm/NcmReportModal';
import { useDeleteNcm } from '../../../core/domain/operational/Ncm/Ncm.hooks';
import { TabContentWrapper } from '@/components/_common';
import NcmExtraFilters from '@/components/Fiscal/Ncm/NcmExtraFilters';
import EditIcon from '@/components/_common/EditIcon';
import DeleteIcon from '@/components/_common/DeleteIcon';
import { ncmMask } from '@/utils/mask/commonMasks';
import { applyMask } from '@/utils/mask';
import { useDebounce } from '@/hooks';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';
import { convertParams, usePersistSearchParams } from '@/utils/queryParams';
import { removeTextMask } from '@/utils/mask/removeTextMask';

const NcmsPage = ({ route }: PageProps) => {
  const intl = useIntl();
  const history = useHistory();

  const [queryParams, setQueryParams] = useState<ListNcmsQuery>({
    order: 'ASC',
    active: true,
    ...convertParams<ListNcmsQuery>({
      active: 'boolean',
      order: 'string',
      description: 'string',
      code: 'number',
    }),
  });

  const queryDebounce = useDebounce(queryParams, 500);
  usePersistSearchParams(queryDebounce);

  const { data, isFetching, fetchNextPage, hasNextPage } = useNcms({
    ...queryDebounce,
    description: removeTextMask(queryDebounce?.description),
  });

  const ncm = flatInfiniteData(data);

  const handleCreateClick = () => {
    history.push('/app/fiscal/ncm/create');
  };

  const [modalReportVisible, setModalReportVisible] = useState(false);

  function toggleReportModal() {
    setModalReportVisible(!modalReportVisible);
  }

  const removeMutation = useDeleteNcm();

  const handleRemoveClick = (ncm: Ncm) => {
    Modal.confirm({
      title: intl.formatMessage({
        id: 'ncm.remove',
      }),
      content: `${intl.formatMessage({
        id: 'ncm.remove.alert',
      })} ${ncm?.description}`,
      onOk: async () => {
        await removeMutation.mutateAsync(ncm.id);
      },
      okType: 'danger',
    });
  };

  const columns: ColumnsType<Ncm> = [
    {
      dataIndex: 'code',
      title: intl.formatMessage({ id: 'ncm.domain.code' }),
      render: (value: string) => {
        if (!value) {
          return '-';
        }

        if (String(value).length >= 0 && String(value).length <= 4) {
          return applyMask(String(value), ncmMask.ncmMask4);
        }

        if (String(value).length >= 4 && String(value).length <= 6) {
          return applyMask(String(value), ncmMask.ncmMask6);
        }

        return applyMask(String(value), ncmMask.ncmMask8);
      },
      width: 200,
    },
    {
      dataIndex: 'description',
      title: intl.formatMessage({ id: 'ncm.domain.description' }),
      render: (value) => value || '-',
      width: 200,
    },
    {
      title: intl.formatMessage({
        id: 'commons.list.header.action',
      }),
      width: 100,
      align: 'right',
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <EditIcon
            disabled={!hasPermissions([Authorities.REGISTER_NCM_EDIT])}
            redirect={`/app/fiscal/ncm/${record.id}`}
          />

          <Divider type="vertical" />

          <DeleteIcon
            onClick={() => handleRemoveClick(record)}
            disabled={!hasPermissions([Authorities.REGISTER_NCM_DELETE])}
          />
        </Space>
      ),
    },
  ];

  return (
    <>
      <Helmet title={intl.formatMessage({ id: route.name })} />

      <TabContentWrapper
        title={route.name}
        permissions={route.permissions}
        extra={
          <Space>
            <Button
              type="primary"
              onClick={() => toggleReportModal()}
              disabled={
                !hasPermissions([Authorities.REGISTER_NCM_REPORT_OF_NCMS])
              }
            >
              <FormattedMessage id="ncm.generateReport" />
            </Button>
            <Button
              type="primary"
              onClick={handleCreateClick}
              disabled={!hasPermissions([Authorities.REGISTER_NCM_ADD])}
            >
              <FormattedMessage id="ncm.create" />
            </Button>
          </Space>
        }
      >
        <AppPageHeader
          onSearch={(value) =>
            setQueryParams((prev) => ({ ...prev, description: value }))
          }
          searchValue={queryParams.description}
          extraFilters={
            <NcmExtraFilters
              queryParams={queryParams}
              setQueryParams={setQueryParams}
            />
          }
        />

        <InfiniteTable<Ncm>
          columns={columns}
          dataSource={ncm}
          loading={isFetching}
          rowKey={(row) => row.id}
          hasNextPage={hasNextPage}
          fetchNextPage={fetchNextPage}
        />

        <NcmReportModal
          visible={modalReportVisible}
          handleVisible={() => toggleReportModal()}
        />
      </TabContentWrapper>
    </>
  );
};

export default NcmsPage;
